import {
  createSettingMetadataFromGroupDefaults,
  getDefaultSettingAgentEditability,
  type SettingMetadataDefaultsContext,
} from "../../settings-catalog/metadata";
import type {
  SettingAgentEditability,
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  SupportFlatTeamSettingGroupById,
  type SupportFlatTeamSettingGroupId,
} from "./groups";
import {
  SUPPORT_CLOSE_ACTION_OPTIONS,
  SUPPORT_CONTACT_FORM_FONT_OPTIONS,
  SUPPORT_CONTACT_FORM_REPLY_EMAIL_INTEGRATION_KIND_OPTIONS,
  SUPPORT_CONTACT_FORM_SUBJECT_INPUT_TYPE_OPTIONS,
  SUPPORT_CSAT_SURVEY_METHOD_OPTIONS,
  SUPPORT_CSAT_SURVEY_SEND_TIME_OPTIONS,
  SUPPORT_DEFAULT_SEND_ACTION_OPTIONS,
  SUPPORT_OPENAI_MODEL_OPTIONS,
  SUPPORT_TICKET_ASSIGNMENT_OPTIONS,
  SUPPORT_TICKET_HANDOFF_TYPE_OPTIONS,
  SUPPORT_VOICE_MESSAGE_TYPE_OPTIONS,
  SUPPORT_VOICE_PROVIDER_OPTIONS,
  SUPPORT_VOICE_RINGING_STRATEGY_OPTIONS,
} from "./selection-options";

interface SupportSettingInput extends SettingRegistryMetadataInput {}

interface SupportSettingRegistryEntry extends SupportSettingInput {
  group: SupportFlatTeamSettingGroupId;
}

function setting(metadata: SupportSettingInput): SupportSettingInput {
  return metadata;
}

function supportGroup<
  const Group extends SupportFlatTeamSettingGroupId,
  const T extends Record<string, SupportSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const SupportFlatSettingCatalogRegistry = {
  ...supportGroup("support.general", {
    "support/enabled": setting({
      label: "Support enabled",
      description: "Turns the support inbox on for this team.",
      risk: "medium",
    }),
    "support/allowUnifiedInbox": setting({
      label: "Unified org inbox",
      description:
        "Shows support tickets from every connected team in the org. When off, the inbox is scoped to this team only.",
    }),
    "support/defaultSendAction": setting({
      label: "Default send action",
      description: "Default action used after an agent sends a reply.",
      valueSemantics: { options: SUPPORT_DEFAULT_SEND_ACTION_OPTIONS },
    }),
    "support/closeAction": setting({
      label: "Close action",
      description: "Action used when a support ticket is closed.",
      valueSemantics: { options: SUPPORT_CLOSE_ACTION_OPTIONS },
    }),
    "support/undoSend": setting({
      label: "Undo send",
      description: "Allows agents to briefly undo sent support replies.",
    }),
    "support/showCreatedDateColumn": setting({
      label: "Show created date column",
      description: "Shows the created date column in support ticket tables.",
    }),
    "support/tags": setting({
      label: "Support tags",
      description: "Tag names and pill styling available in the support inbox.",
    }),
  }),

  ...supportGroup("support.email_integrations", {
    "support/defaultEmail": setting({
      label: "Default support email",
      description: "Default reply address for support tickets.",
      synonyms: [
        "support email address",
        "reply email",
        "default reply address",
        "gmail",
        "outlook",
      ],
      valueSemantics: { inputKind: "email" },
    }),
    "support/emailSignature": setting({
      label: "Email signature",
      description: "Signature appended to human support replies.",
      synonyms: ["support email signature", "agent email signature"],
    }),
    "support/excludedEmails": setting({
      label: "Excluded emails",
      description:
        "Email addresses that should be excluded from support handling.",
      synonyms: [
        "email exclusions",
        "blocked support emails",
        "ignored email addresses",
        "excluded support senders",
      ],
    }),
    "support/senderEmails": setting({
      label: "Sender emails",
      description:
        "Verified sender email identities available for support replies.",
      synonyms: [
        "sender identities",
        "from addresses",
        "connected email addresses",
        "email integrations",
        "gmail",
        "outlook",
      ],
      risk: "medium",
    }),
    "support/tagSpam": setting({
      label: "Spam detection",
      description:
        "Automatically closes and tags tickets that the connected email provider marks as spam.",
      synonyms: [
        "close spam emails automatically",
        "gmail spam",
        "email provider spam",
        "spam email",
        "spam tagging",
      ],
      risk: {
        level: "medium",
        reason:
          "Changes can automatically close support tickets based on provider spam classification.",
      },
    }),
  }),

  ...supportGroup("support.ticket_routing", {
    "support/autoMergeExclusions": setting({
      label: "Auto-merge exclusions",
      description: "Senders excluded from automatic ticket merging.",
    }),
    "support/autoSplitSenders": setting({
      label: "Auto-split senders",
      description: "Senders whose messages should be split into new tickets.",
    }),
    "support/maxTicketsPerUser": setting({
      label: "Max tickets per user",
      description:
        "Maximum number of tickets assigned to a user during auto-assignment.",
    }),
    "support/ticketAssignment": setting({
      label: "Ticket assignment mode",
      description: "Assignment strategy for new support tickets.",
      valueSemantics: { options: SUPPORT_TICKET_ASSIGNMENT_OPTIONS },
    }),
    "support/usersForAutoAssignment": setting({
      label: "Auto-assignment users",
      description:
        "Users eligible to receive automatically assigned support tickets.",
    }),
    "support/autoAssignOnResponse": setting({
      label: "Auto-assign on response",
      description: "Assigns tickets automatically when an agent responds.",
    }),
    "support/autoReassignOnResponse": setting({
      label: "Auto-reassign on response",
      description:
        "Reassigns tickets automatically when a different agent responds.",
    }),
    "support/autoReassignFromAiOnResponse": setting({
      label: "Reassign from AI on response",
      description: "Moves tickets away from AI when a human agent responds.",
    }),
    "support/autoReassignFromHumanOnResponse": setting({
      label: "Reassign from human on response",
      description:
        "Moves tickets from a human assignee when another agent responds.",
    }),
    "support/unassignOpenTicketsAtShiftEnd": setting({
      label: "Unassign open tickets at shift end",
      description:
        "Clears a user's open tickets back to unassigned when their work schedule shift ends.",
    }),
    "support/keepAssigneeOnTicketReopen": setting({
      label: "Keep assignee on reopen",
      description: "Keeps the current assignee when a closed ticket reopens.",
    }),
    "support/useInProgressStatus": setting({
      label: "Use in-progress status",
      description: "Enables an in-progress support ticket status.",
    }),
    "support/autoMergeEnabled": setting({
      label: "Auto-merge enabled",
      description:
        "Automatically merges related support messages into existing tickets.",
    }),
    "support/propagateDeletes": setting({
      label: "Propagate deletes",
      description:
        "Propagates message deletion behavior across linked support systems.",
      risk: "medium",
    }),
    "support/closeTicketsOnGmailArchive": setting({
      label: "Close tickets on Gmail archive",
      description:
        "Closes support tickets when matching Gmail threads are archived.",
      relatedGroups: ["support.email_integrations"],
      synonyms: [
        "gmail archive",
        "archive gmail thread",
        "gmail integration",
        "close archived gmail tickets",
      ],
    }),
    "support/autoCloseArchivedGmailMessages": setting({
      label: "Auto-close archived Gmail messages",
      description: "Automatically closes tickets for archived Gmail messages.",
      relatedGroups: ["support.email_integrations"],
      visibility: "admin",
      synonyms: [
        "gmail archive",
        "archived gmail messages",
        "gmail integration",
        "auto close gmail",
      ],
    }),
  }),

  ...supportGroup("support.ai", {
    "support/aiEmailSignature": setting({
      label: "AI email signature",
      description: "Signature appended to AI-generated support email replies.",
      relatedGroups: ["support.email_integrations"],
      synonyms: ["support AI email signature", "AI reply signature"],
    }),
    "support/ai/enabled": setting({
      label: "Support AI enabled",
      description: "Turns on AI capabilities for support.",
      risk: "medium",
    }),
    "support/ai/respondToInternalEmails": setting({
      label: "Respond to internal emails",
      description:
        "Allows support AI to respond to internal email conversations.",
      risk: "medium",
    }),
    "support/ai/copilotEnabled": setting({
      label: "Copilot enabled",
      description: "Enables AI draft assistance for human support agents.",
    }),
    "support/ai/copilotModel": setting({
      label: "Copilot model",
      description: "Model used for AI copilot drafts in support.",
      valueSemantics: {
        options: SUPPORT_OPENAI_MODEL_OPTIONS,
        unsetLabel: "Default model",
      },
    }),
    "support/ai/autoGenerateResponses": setting({
      label: "Auto-generate responses",
      description:
        "Automatically generates AI draft responses for eligible tickets.",
    }),
    "support/ai/useEmailSignature": setting({
      label: "Use AI email signature",
      description:
        "Appends the configured AI email signature to AI support replies.",
    }),
    "support/ai/autopilotEnabled": setting({
      label: "Autopilot enabled",
      description:
        "Allows AI to resolve eligible support tickets automatically.",
      synonyms: [
        "support autopilot",
        "AI autopilot",
        "autopilot for support tickets",
        "automatically resolve support tickets",
      ],
      risk: {
        level: "high",
        reason: "AI may resolve customer support tickets without human review.",
      },
    }),
    "support/ai/automateLiveChat": setting({
      label: "Automate live chat",
      description: "Allows AI to handle eligible live chat conversations.",
      relatedGroups: ["support.live_chat_widget"],
      risk: {
        level: "high",
        reason: "AI may respond to live-chat customers in real time.",
      },
    }),
    "support/ai/autoCloseNoReplyTickets": setting({
      label: "Auto-close no-reply tickets",
      description:
        "Allows AI automation to close tickets that do not need a reply.",
      risk: "medium",
    }),
    "support/ai/aiAutopilotTicketVolume": setting({
      label: "Autopilot ticket volume",
      description: "Ticket volume limit or allocation for AI autopilot.",
      risk: "medium",
    }),
    "support/ai/trialMessagesRemaining": setting({
      label: "Trial messages remaining",
      description: "Internal counter for remaining AI support trial messages.",
      visibility: "restricted",
    }),
    "support/ai/hasTriedAi": setting({
      label: "Has tried AI",
      description:
        "Internal flag tracking whether the team has tried support AI.",
      visibility: "restricted",
    }),
    "support/ai/whitelistedDomains": setting({
      label: "Whitelisted domains",
      description:
        "Domains allowed for support AI behavior that is domain-scoped.",
    }),
    "support/ai/whitelistedEmails": setting({
      label: "Whitelisted emails",
      description:
        "Email addresses allowed for support AI behavior that is email-scoped.",
    }),
    "support/ai/botGreeting": setting({
      label: "Bot greeting",
      description: "Greeting shown or sent by the support AI bot.",
    }),
    "support/ai/humanHandoffMessage": setting({
      label: "Human handoff message",
      description:
        "Message used when support AI hands a conversation to a human.",
      relatedGroups: ["support.concierge_handoff"],
      risk: "medium",
    }),
    "support/ai/emailVerificationRequired": setting({
      label: "Require email verification",
      description:
        "Requires customer email verification before support AI proceeds.",
      risk: "medium",
    }),
    "support/ai/ticketDescription": setting({
      label: "Ticket description",
      description:
        "Instructions or description used by support AI when handling tickets.",
    }),
    "support/ai/disallowedPrompts": setting({
      label: "Disallowed prompts",
      description: "Prompts or topics support AI should refuse or avoid.",
      risk: "medium",
    }),
    "support/ai/disallowedPromptsEnabled": setting({
      label: "Disallowed prompts enabled",
      description:
        "Enforces the configured disallowed prompt list for support AI.",
      risk: "medium",
    }),
    "support/ai/autopilotToolSettings/cancelOrder/cancelOrderTimeWindowAfterPlacingMinutes":
      setting({
        label: "Cancel order window",
        description:
          "Minutes after order placement when support AI may cancel an order.",
        risk: {
          level: "high",
          reason: "Controls when AI is allowed to cancel customer orders.",
        },
      }),
    "support/ai/autopilotToolSettings/updateOrderAddress/updateOrderAddressTimeWindowAfterPlacingMinutes":
      setting({
        label: "Update order address window",
        description:
          "Minutes after order placement when support AI may update an order address.",
        risk: {
          level: "high",
          reason:
            "Controls when AI is allowed to change customer order addresses.",
        },
      }),
  }),

  ...supportGroup("support.ai_channels", {
    "support/ai/enabledPlatforms/email": setting({
      label: "Email AI enabled",
      description: "Allows support AI on email conversations.",
    }),
    "support/ai/enabledPlatforms/facebook": setting({
      label: "Facebook AI enabled",
      description: "Allows support AI on Facebook DM conversations.",
    }),
    "support/ai/enabledPlatforms/facebookComments": setting({
      label: "Facebook comments AI enabled",
      description:
        "Allows support AI to auto-reply to public Facebook comments.",
    }),
    "support/ai/enabledPlatforms/instagram": setting({
      label: "Instagram AI enabled",
      description: "Allows support AI on Instagram DM conversations.",
    }),
    "support/ai/enabledPlatforms/instagramComments": setting({
      label: "Instagram comments AI enabled",
      description:
        "Allows support AI to auto-reply to public Instagram comments.",
    }),
    "support/ai/enabledPlatforms/sms": setting({
      label: "SMS AI enabled",
      description: "Allows support AI on SMS conversations.",
    }),
    "support/ai/enabledPlatforms/tiktok": setting({
      label: "TikTok AI enabled",
      description: "Allows support AI on TikTok conversations.",
    }),
    "support/ai/enabledPlatforms/attentive": setting({
      label: "Attentive AI enabled",
      description: "Allows support AI on Attentive conversations.",
    }),
    "support/ai/enabledPlatforms/postscript": setting({
      label: "Postscript AI enabled",
      description: "Allows support AI on Postscript conversations.",
    }),
  }),

  ...supportGroup("support.billing", {
    "support/billing/base/pricePerMonthInCents": setting({
      label: "Base monthly price",
      description: "Monthly base support subscription price in cents.",
    }),
    "support/billing/base/subscriptionActive": setting({
      label: "Base subscription active",
      description: "Whether the base support subscription is active.",
    }),
    "support/billing/base/createdAt": setting({
      label: "Base billing created at",
      description: "Creation timestamp for the base support billing record.",
    }),
    "support/billing/base/updatedAt": setting({
      label: "Base billing updated at",
      description: "Last update timestamp for the base support billing record.",
    }),
    "support/billing/base/billingStartDate": setting({
      label: "Base billing start date",
      description: "Start date for base support billing.",
    }),
    "support/billing/base/freeTrialEndDate": setting({
      label: "Base free trial end date",
      description: "End date for the base support free trial.",
    }),
    "support/billing/base/version": setting({
      label: "Base billing version",
      description:
        "Internal version for the base support billing configuration.",
    }),
    "support/billing/ai/aiResolutionPriceInCents": setting({
      label: "AI resolution price",
      description: "Price in cents for each billable AI support resolution.",
    }),
    "support/billing/ai/atShopifyCap": setting({
      label: "At Shopify billing cap",
      description:
        "Whether AI support billing has reached the Shopify billing cap.",
    }),
    "support/billing/ai/billingAccepted": setting({
      label: "AI billing accepted",
      description: "Whether the merchant has accepted AI support billing.",
    }),
    "support/billing/ai/billingStartDate": setting({
      label: "AI billing start date",
      description: "Start date for AI support billing.",
    }),
    "support/billing/ai/freeTrialDays": setting({
      label: "AI free trial days",
      description: "Number of free trial days for AI support billing.",
    }),
    "support/billing/ai/freeTrialEndDate": setting({
      label: "AI free trial end date",
      description: "End date for the AI support free trial.",
    }),
    "support/billing/ai/freeTrialUsed": setting({
      label: "AI free trial used",
      description: "Whether the AI support free trial has been used.",
    }),
    "support/billing/ai/ticketsUsed": setting({
      label: "AI tickets used",
      description: "Number of AI support tickets counted for billing.",
    }),
    "support/billing/ai/createdAt": setting({
      label: "AI billing created at",
      description: "Creation timestamp for the AI support billing record.",
    }),
    "support/billing/ai/updatedAt": setting({
      label: "AI billing updated at",
      description: "Last update timestamp for the AI support billing record.",
    }),
    "support/billing/ai/pricePerMonth": setting({
      label: "AI monthly price",
      description: "Monthly price for AI support billing.",
    }),
    "support/billing/ai/ticketsCovered": setting({
      label: "AI tickets covered",
      description: "Number of AI support tickets included in the billing plan.",
    }),
    "support/billing/textMessaging/enabled": setting({
      label: "Text billing enabled",
      description: "Whether support text messaging billing is enabled.",
    }),
    "support/billing/textMessaging/billingFailed": setting({
      label: "Text billing failed",
      description: "Whether the current text messaging billing attempt failed.",
    }),
    "support/billing/textMessaging/subscriptionActive": setting({
      label: "Text subscription active",
      description: "Whether the support text messaging subscription is active.",
    }),
    "support/billing/textMessaging/monthlyMinimumInTenthCents": setting({
      label: "Text monthly minimum",
      description: "Monthly minimum for support text billing in tenth cents.",
    }),
    "support/billing/textMessaging/pricePerSMSInTenthCents": setting({
      label: "SMS unit price",
      description: "Price per support SMS message in tenth cents.",
    }),
    "support/billing/textMessaging/pricePerMMSInTenthCents": setting({
      label: "MMS unit price",
      description: "Price per support MMS message in tenth cents.",
    }),
    "support/billing/textMessaging/previousCycles": setting({
      label: "Previous text billing cycles",
      description: "Historical support text messaging billing cycles.",
    }),
    "support/billing/textMessaging/currentBillingCycle/billingCycleStartDate":
      setting({
        label: "Current text billing cycle start",
        description: "Start date for the current support text billing cycle.",
      }),
    "support/billing/textMessaging/currentBillingCycle/lastBilledAt": setting({
      label: "Text last billed at",
      description: "Timestamp of the last support text billing charge.",
    }),
    "support/billing/textMessaging/currentBillingCycle/monthlyMinimumRemainingInTenthCents":
      setting({
        label: "Text monthly minimum remaining",
        description:
          "Remaining monthly minimum for support text billing in tenth cents.",
      }),
    "support/billing/textMessaging/currentBillingCycle/billed/smsMessagesReceived":
      setting({
        label: "Billed SMS received",
        description: "SMS messages received and billed in the current cycle.",
      }),
    "support/billing/textMessaging/currentBillingCycle/billed/smsMessagesSent":
      setting({
        label: "Billed SMS sent",
        description: "SMS messages sent and billed in the current cycle.",
      }),
    "support/billing/textMessaging/currentBillingCycle/billed/mmsMessagesReceived":
      setting({
        label: "Billed MMS received",
        description: "MMS messages received and billed in the current cycle.",
      }),
    "support/billing/textMessaging/currentBillingCycle/billed/mmsMessagesSent":
      setting({
        label: "Billed MMS sent",
        description: "MMS messages sent and billed in the current cycle.",
      }),
    "support/billing/textMessaging/unbilled/smsMessagesReceived": setting({
      label: "Unbilled SMS received",
      description: "SMS messages received but not yet billed.",
    }),
    "support/billing/textMessaging/unbilled/smsMessagesSent": setting({
      label: "Unbilled SMS sent",
      description: "SMS messages sent but not yet billed.",
    }),
    "support/billing/textMessaging/unbilled/mmsMessagesReceived": setting({
      label: "Unbilled MMS received",
      description: "MMS messages received but not yet billed.",
    }),
    "support/billing/textMessaging/unbilled/mmsMessagesSent": setting({
      label: "Unbilled MMS sent",
      description: "MMS messages sent but not yet billed.",
    }),
    "support/billing/voice/billingStartDate": setting({
      label: "Voice billing start date",
      description: "Start date for support voice billing.",
    }),
    "support/billing/voice/createdAt": setting({
      label: "Voice billing created at",
      description: "Creation timestamp for the support voice billing record.",
    }),
    "support/billing/voice/updatedAt": setting({
      label: "Voice billing updated at",
      description:
        "Last update timestamp for the support voice billing record.",
    }),
    "support/billing/voice/pricePerMinuteInCents": setting({
      label: "Voice per-minute price",
      description: "Per-minute support voice price in cents.",
    }),
    "support/billing/voice/pricePerMonthInCents": setting({
      label: "Voice monthly price",
      description: "Monthly support voice price in cents.",
    }),
    "support/billing/voice/subscriptionActive": setting({
      label: "Voice subscription active",
      description: "Whether the support voice subscription is active.",
    }),
  }),

  ...supportGroup("support.voice", {
    "support/voice/enabled": setting({
      label: "Voice support enabled",
      description: "Turns on voice support for this team.",
      risk: "medium",
    }),
    "support/voice/provider": setting({
      label: "Voice provider",
      description: "Provider used for support voice calls.",
      valueSemantics: { options: SUPPORT_VOICE_PROVIDER_OPTIONS },
      risk: {
        level: "high",
        reason: "Changing providers can alter call routing and provider setup.",
      },
    }),
    "support/voice/availableHoursEnabled": setting({
      label: "Voice hours enabled",
      description: "Uses availability hours for support voice routing.",
    }),
    "support/voice/availableHoursTimezone": setting({
      label: "Voice hours timezone",
      description: "Timezone used for support voice availability hours.",
    }),
    "support/voice/ringingStrategy": setting({
      label: "Ringing strategy",
      description: "How incoming support calls ring available agents.",
      valueSemantics: { options: SUPPORT_VOICE_RINGING_STRATEGY_OPTIONS },
    }),
    "support/voice/allowHoldingCallsInQueue": setting({
      label: "Allow queued calls",
      description: "Allows callers to wait in a hold queue.",
    }),
    "support/voice/sendToVoicemailWhenAllAgentsAreOffline": setting({
      label: "Send offline calls to voicemail",
      description: "Sends callers to voicemail when all agents are offline.",
    }),
    "support/voice/playAutomatedMessageWhenAllAgentsAreOffline": setting({
      label: "Play offline automated message",
      description: "Plays an automated message when all agents are offline.",
    }),
    "support/voice/holdMusicName": setting({
      label: "Hold music name",
      description: "Display name for the configured hold music.",
    }),
    "support/voice/holdMusicUrl": setting({
      label: "Hold music URL",
      description: "Audio URL used for support voice hold music.",
      valueSemantics: { inputKind: "url" },
    }),
    "support/voice/billingGroupId": setting({
      label: "Voice billing group ID",
      description: "Internal voice provider billing group identifier.",
      visibility: "restricted",
      risk: "high",
    }),
    "support/voice/sipConnectionId": setting({
      label: "SIP connection ID",
      description: "Internal SIP connection identifier for voice support.",
      visibility: "restricted",
      risk: "high",
    }),
    "support/voice/sipConnectionCredential": setting({
      label: "SIP connection credential",
      description: "Internal SIP credential reference for voice support.",
      visibility: "restricted",
      risk: "high",
    }),
    "support/voice/voiceApplicationId": setting({
      label: "Voice application ID",
      description:
        "Internal provider application identifier for voice support.",
      visibility: "restricted",
      risk: "high",
    }),
    "support/voice/outboundVoiceProfileId": setting({
      label: "Outbound voice profile ID",
      description: "Internal outbound voice profile identifier.",
      visibility: "restricted",
      risk: "high",
    }),
    "support/voice/availableHours/monday": setting({
      label: "Monday voice hours",
      description: "Monday availability schedule for support voice.",
    }),
    "support/voice/availableHours/tuesday": setting({
      label: "Tuesday voice hours",
      description: "Tuesday availability schedule for support voice.",
    }),
    "support/voice/availableHours/wednesday": setting({
      label: "Wednesday voice hours",
      description: "Wednesday availability schedule for support voice.",
    }),
    "support/voice/availableHours/thursday": setting({
      label: "Thursday voice hours",
      description: "Thursday availability schedule for support voice.",
    }),
    "support/voice/availableHours/friday": setting({
      label: "Friday voice hours",
      description: "Friday availability schedule for support voice.",
    }),
    "support/voice/availableHours/saturday": setting({
      label: "Saturday voice hours",
      description: "Saturday availability schedule for support voice.",
    }),
    "support/voice/availableHours/sunday": setting({
      label: "Sunday voice hours",
      description: "Sunday availability schedule for support voice.",
    }),
    "support/voice/inboundGreetingMessage/enabled": setting({
      label: "Inbound greeting enabled",
      description: "Plays an inbound greeting for support voice calls.",
    }),
    "support/voice/inboundGreetingMessage/type": setting({
      label: "Inbound greeting type",
      description: "Type of inbound voice greeting to play.",
      valueSemantics: { options: SUPPORT_VOICE_MESSAGE_TYPE_OPTIONS },
    }),
    "support/voice/inboundGreetingMessage/ttsMessage": setting({
      label: "Inbound greeting text",
      description: "Text-to-speech message for inbound support call greetings.",
    }),
    "support/voice/inboundGreetingMessage/audioName": setting({
      label: "Inbound greeting audio name",
      description: "Audio asset name for inbound support call greetings.",
    }),
    "support/voice/inboundGreetingMessage/audioUrl": setting({
      label: "Inbound greeting audio URL",
      description: "Audio asset URL for inbound support call greetings.",
      valueSemantics: { inputKind: "url" },
    }),
    "support/voice/outboundGreetingMessage/enabled": setting({
      label: "Outbound greeting enabled",
      description: "Plays an outbound greeting for support voice calls.",
    }),
    "support/voice/outboundGreetingMessage/type": setting({
      label: "Outbound greeting type",
      description: "Type of outbound voice greeting to play.",
      valueSemantics: { options: SUPPORT_VOICE_MESSAGE_TYPE_OPTIONS },
    }),
    "support/voice/outboundGreetingMessage/ttsMessage": setting({
      label: "Outbound greeting text",
      description:
        "Text-to-speech message for outbound support call greetings.",
    }),
    "support/voice/outboundGreetingMessage/audioName": setting({
      label: "Outbound greeting audio name",
      description: "Audio asset name for outbound support call greetings.",
    }),
    "support/voice/outboundGreetingMessage/audioUrl": setting({
      label: "Outbound greeting audio URL",
      description: "Audio asset URL for outbound support call greetings.",
      valueSemantics: { inputKind: "url" },
    }),
    "support/voice/offHoursMessage/enabled": setting({
      label: "Off-hours message enabled",
      description: "Plays an off-hours message for support voice calls.",
    }),
    "support/voice/offHoursMessage/type": setting({
      label: "Off-hours message type",
      description: "Type of off-hours voice message to play.",
      valueSemantics: { options: SUPPORT_VOICE_MESSAGE_TYPE_OPTIONS },
    }),
    "support/voice/offHoursMessage/ttsMessage": setting({
      label: "Off-hours message text",
      description: "Text-to-speech message for support voice off-hours calls.",
    }),
    "support/voice/offHoursMessage/audioName": setting({
      label: "Off-hours audio name",
      description: "Audio asset name for support voice off-hours calls.",
    }),
    "support/voice/offHoursMessage/audioUrl": setting({
      label: "Off-hours audio URL",
      description: "Audio asset URL for support voice off-hours calls.",
      valueSemantics: { inputKind: "url" },
    }),
    "support/voice/voicemailMessage/enabled": setting({
      label: "Voicemail message enabled",
      description: "Plays a voicemail message for support voice calls.",
    }),
    "support/voice/voicemailMessage/type": setting({
      label: "Voicemail message type",
      description: "Type of voicemail message to play.",
      valueSemantics: { options: SUPPORT_VOICE_MESSAGE_TYPE_OPTIONS },
    }),
    "support/voice/voicemailMessage/ttsMessage": setting({
      label: "Voicemail message text",
      description: "Text-to-speech message for support voice voicemail.",
    }),
    "support/voice/voicemailMessage/audioName": setting({
      label: "Voicemail audio name",
      description: "Audio asset name for support voice voicemail.",
    }),
    "support/voice/voicemailMessage/audioUrl": setting({
      label: "Voicemail audio URL",
      description: "Audio asset URL for support voice voicemail.",
      valueSemantics: { inputKind: "url" },
    }),
  }),

  ...supportGroup("support.contact_form", {
    "support/contactForm/enabled": setting({
      label: "Contact form enabled",
      description: "Turns on the hosted support contact form.",
      risk: "medium",
    }),
    "support/contactForm/title": setting({
      label: "Contact form title",
      description: "Heading shown on the hosted support contact form.",
    }),
    "support/contactForm/successMessage": setting({
      label: "Contact form success message",
      description: "Message shown after a customer submits the contact form.",
    }),
    "support/contactForm/customCss": setting({
      label: "Contact form custom CSS",
      description: "Custom CSS applied to the hosted contact form.",
      risk: {
        level: "medium",
        reason: "Invalid CSS can break the hosted contact form layout.",
      },
    }),
    "support/contactForm/allowFileUpload": setting({
      label: "Allow file upload",
      description:
        "Allows customers to attach files to contact form submissions.",
    }),
    "support/contactForm/customFields": setting({
      label: "Contact form custom fields",
      description: "Additional custom fields shown on the contact form.",
      risk: {
        level: "medium",
        reason:
          "Field changes affect what information customers are asked to submit.",
      },
    }),
    "support/contactForm/font": setting({
      label: "Contact form font",
      description: "Font family used by the hosted contact form.",
      valueSemantics: { options: SUPPORT_CONTACT_FORM_FONT_OPTIONS },
    }),
    "support/contactForm/collapsibleForm": setting({
      label: "Collapsible contact form",
      description: "Allows sections of the contact form to collapse.",
    }),
    "support/contactForm/showActions/quickActions": setting({
      label: "Show quick actions",
      description: "Shows quick actions on the contact form.",
    }),
    "support/contactForm/showActions/orderEditing": setting({
      label: "Show order editing action",
      description: "Shows order editing as a contact form action.",
    }),
    "support/contactForm/showActions/returnPortal": setting({
      label: "Show return portal action",
      description: "Shows return portal access as a contact form action.",
    }),
    "support/contactForm/showActions/faq": setting({
      label: "Show FAQ action",
      description: "Shows FAQ access as a contact form action.",
    }),
    "support/contactForm/submitButton/label": setting({
      label: "Submit button label",
      description: "Text shown on the contact form submit button.",
    }),
    "support/contactForm/submitButton/color": setting({
      label: "Submit button color",
      description: "Color of the contact form submit button.",
      valueSemantics: { inputKind: "color" },
    }),
    "support/contactForm/name/placeholder": setting({
      label: "Name placeholder",
      description: "Placeholder text for the contact form name field.",
    }),
    "support/contactForm/name/required": setting({
      label: "Name required",
      description: "Requires customers to provide a name on the contact form.",
    }),
    "support/contactForm/name/show": setting({
      label: "Show name field",
      description: "Shows the name field on the contact form.",
    }),
    "support/contactForm/email/placeholder": setting({
      label: "Email placeholder",
      description: "Placeholder text for the contact form email field.",
    }),
    "support/contactForm/phone/placeholder": setting({
      label: "Phone placeholder",
      description: "Placeholder text for the contact form phone field.",
    }),
    "support/contactForm/phone/required": setting({
      label: "Phone required",
      description:
        "Requires customers to provide a phone number on the contact form.",
    }),
    "support/contactForm/phone/show": setting({
      label: "Show phone field",
      description: "Shows the phone field on the contact form.",
    }),
    "support/contactForm/subject/placeholder": setting({
      label: "Subject placeholder",
      description: "Placeholder text for the contact form subject field.",
    }),
    "support/contactForm/subject/required": setting({
      label: "Subject required",
      description:
        "Requires customers to provide a subject on the contact form.",
    }),
    "support/contactForm/subject/show": setting({
      label: "Show subject field",
      description: "Shows the subject field on the contact form.",
    }),
    "support/contactForm/subject/options": setting({
      label: "Subject options",
      description: "Dropdown options for the contact form subject field.",
    }),
    "support/contactForm/subject/inputType": setting({
      label: "Subject input type",
      description: "Input style used for the contact form subject field.",
      valueSemantics: {
        options: SUPPORT_CONTACT_FORM_SUBJECT_INPUT_TYPE_OPTIONS,
      },
    }),
    "support/contactForm/comment/placeholder": setting({
      label: "Comment placeholder",
      description: "Placeholder text for the contact form comment field.",
    }),
    "support/contactForm/replyEmail/email": setting({
      label: "Reply email address",
      description: "Email address used for contact form reply identity.",
      valueSemantics: { inputKind: "email" },
    }),
    "support/contactForm/replyEmail/name": setting({
      label: "Reply email name",
      description: "Display name used for contact form reply identity.",
    }),
    "support/contactForm/replyEmail/integrationKind": setting({
      label: "Reply email integration",
      description: "Integration kind used for contact form replies.",
      valueSemantics: {
        options: SUPPORT_CONTACT_FORM_REPLY_EMAIL_INTEGRATION_KIND_OPTIONS,
        unsetLabel: "Use default reply identity",
      },
    }),
    "customerWidget/general/widgetQuickActions": setting({
      label: "Widget quick actions",
      description:
        "Quick actions shown in the customer widget and contact form.",
      relatedGroups: ["support.live_chat_widget"],
      risk: {
        level: "medium",
        reason:
          "Quick action changes alter customer navigation from the widget and contact form.",
      },
    }),
  }),

  ...supportGroup("support.csat", {
    "support/csat/enabled": setting({
      label: "CSAT enabled",
      description: "Turns on customer satisfaction surveys for support.",
    }),
    "support/csat/allowRemarks": setting({
      label: "Allow CSAT remarks",
      description: "Allows customers to leave written remarks on CSAT surveys.",
    }),
    "support/csat/surveyMessage": setting({
      label: "CSAT survey message",
      description: "Message shown with support customer satisfaction surveys.",
    }),
    "support/csat/surveySendTime": setting({
      label: "CSAT send time",
      description: "When support customer satisfaction surveys are sent.",
      valueSemantics: { options: SUPPORT_CSAT_SURVEY_SEND_TIME_OPTIONS },
    }),
    "support/csat/surveyMethods": setting({
      label: "CSAT survey methods",
      description:
        "Channels used to send support customer satisfaction surveys.",
      valueSemantics: {
        options: SUPPORT_CSAT_SURVEY_METHOD_OPTIONS,
        selectionMode: "multiple",
      },
    }),
    "support/csat/headerImage": setting({
      label: "CSAT header image",
      description:
        "Header image shown on support customer satisfaction surveys.",
      valueSemantics: { inputKind: "url" },
    }),
  }),

  ...supportGroup("support.social", {
    "support/facebook/autoCloseExternal": setting({
      label: "Auto-close external Facebook conversations",
      description:
        "Automatically closes Facebook conversations handled externally.",
    }),
    "support/facebook/commentsEnabled": setting({
      label: "Facebook comments enabled",
      description: "Turns on Facebook comment handling in support.",
    }),
    "support/instagram/autoCloseExternal": setting({
      label: "Auto-close external Instagram conversations",
      description:
        "Automatically closes Instagram conversations handled externally.",
    }),
    "support/instagram/commentsEnabled": setting({
      label: "Instagram comments enabled",
      description: "Turns on Instagram comment handling in support.",
    }),
    "support/instagram/autoCloseMentions": setting({
      label: "Auto-close Instagram mentions",
      description: "Automatically closes Instagram mention conversations.",
    }),
    "support/instagram/autoCloseRepliedStories": setting({
      label: "Auto-close replied Instagram stories",
      description:
        "Automatically closes Instagram story replies after response.",
    }),
    "support/metaPagesData/pagesData": setting({
      label: "Meta pages data",
      description: "Connected Meta page data used for support social channels.",
      visibility: "restricted",
    }),
  }),

  ...supportGroup("support.sms", {
    "support/sms/enabled": setting({
      label: "SMS support enabled",
      description: "Turns on SMS support.",
      risk: "medium",
    }),
    "support/sms/privacyPolicyUrl": setting({
      label: "SMS privacy policy URL",
      description: "Privacy policy URL used for SMS support compliance.",
      valueSemantics: { inputKind: "url" },
    }),
    "support/sms/termsAndConditionsUrl": setting({
      label: "SMS terms URL",
      description: "Terms and conditions URL used for SMS support compliance.",
      valueSemantics: { inputKind: "url" },
    }),
  }),

  ...supportGroup("support.faq", {
    "support/faq/actionButtons": setting({
      label: "FAQ action buttons",
      description:
        "The quick action buttons under the FAQ search bar. Track a package, Start a return or exchange, and Contact us show by default; each one's text, icon, and link can be changed, and extra buttons can be added.",
      synonyms: [
        "quick actions",
        "help center buttons",
        "track a package button",
        "start a return button",
        "contact us button",
        "custom faq button",
        "faq button link",
        "faq button icon",
      ],
      risk: "medium",
      changeGuidance: {
        summary:
          "Buttons render in the configured order. A button linked to a full URL opens in a new tab; a link starting with / stays in the customer portal.",
        warning:
          "Contact us falls back to opening the chat widget, so it disappears if the widget is off in the returns portal and no link is set.",
      },
    }),
    "support/faq/brandKitOverrides/pageBackgroundColor": setting({
      label: "Override FAQ page background",
      description:
        "Allows FAQ page background color to override the brand kit.",
    }),
    "support/faq/collectionBackgroundColor": setting({
      label: "FAQ collection background color",
      description: "Background color for FAQ collection cards.",
      valueSemantics: { inputKind: "color" },
    }),
    "support/faq/collectionBorderColor": setting({
      label: "FAQ collection border color",
      description: "Border color for FAQ collection cards.",
      valueSemantics: { inputKind: "color" },
    }),
    "support/faq/collectionCornerRadius": setting({
      label: "FAQ collection corner radius",
      description: "Corner radius for FAQ collection cards.",
    }),
    "support/faq/displayCollectionIcon": setting({
      label: "Display FAQ collection icon",
      description: "Shows icons on FAQ collection cards.",
    }),
    "support/faq/displayCollectionSubtext": setting({
      label: "Display FAQ collection subtext",
      description: "Shows supporting text on FAQ collection cards.",
    }),
    "support/faq/headlineText": setting({
      label: "FAQ headline text",
      description: "Headline shown on the FAQ page.",
    }),
    "support/faq/headlineTextColor": setting({
      label: "FAQ headline color",
      description: "Text color for the FAQ page headline.",
      valueSemantics: { inputKind: "color" },
    }),
    "support/faq/homeBackgroundImageUrl": setting({
      label: "FAQ home background image",
      description: "Background image URL for the FAQ home page.",
      valueSemantics: { inputKind: "url" },
    }),
    "support/faq/overlayColor": setting({
      label: "FAQ overlay color",
      description: "Overlay color applied to the FAQ home background image.",
      valueSemantics: { inputKind: "color" },
    }),
    "support/faq/overlayOpacity": setting({
      label: "FAQ overlay opacity",
      description: "Opacity of the FAQ home background image overlay.",
    }),
    "support/faq/pageBackgroundColor": setting({
      label: "FAQ page background color",
      description: "Background color for the FAQ page.",
      valueSemantics: { inputKind: "color" },
    }),
  }),

  ...supportGroup("support.live_chat_widget", {
    "customerWidget/support/chatExpirationMessage": setting({
      label: "Chat expiration message",
      description: "Message shown when an inactive support chat expires.",
      valueSemantics: { inputKind: "textarea" },
    }),
    "customerWidget/support/chatExpirationMinutes": setting({
      label: "Chat expiration minutes",
      description: "Minutes of inactivity before a support chat expires.",
    }),
    "customerWidget/support/emailConversionPrepMessage": setting({
      label: "Email conversion prep message",
      description: "Message shown before converting live chat to email.",
      valueSemantics: { inputKind: "textarea" },
      // Also edited from the Concierge Handoff editor, so it must load when
      // AskRedo opens that group on its own.
      relatedGroups: ["support.concierge_handoff"],
    }),
    "customerWidget/support/liveChatConversionPrepMessage": setting({
      label: "Live chat conversion prep message",
      description: "Message shown before routing a conversation to live chat.",
      valueSemantics: { inputKind: "textarea" },
      relatedGroups: ["support.concierge_handoff"],
    }),
    "customerWidget/support/hideSupportWidgetFeatures": setting({
      label: "Hide support widget features",
      description:
        "Hides support-specific features inside the customer widget.",
    }),
    "customerWidget/support/messagingHeadingText": setting({
      label: "Messaging heading text",
      description:
        "Heading text shown in the support area of the customer widget.",
    }),
    "customerWidget/support/unresponsiveConversionEnabled": setting({
      label: "Unresponsive conversion enabled",
      description:
        "Converts unresponsive live chats into another support channel.",
      risk: {
        level: "medium",
        reason:
          "Changes how abandoned live chats are converted into follow-up support.",
      },
    }),
    "customerWidget/support/unresponsiveConversionMinutes": setting({
      label: "Unresponsive conversion minutes",
      description: "Minutes before an unresponsive live chat is converted.",
    }),
    "customerWidget/support/unresponsiveConversionSeconds": setting({
      label: "Unresponsive conversion seconds",
      description:
        "Additional seconds before an unresponsive live chat is converted.",
    }),
    "customerWidget/general/hideWidgetAfterHours": setting({
      label: "Hide widget after hours",
      description:
        "Hides the customer widget outside configured support hours.",
      synonyms: [
        "hide support widget after hours",
        "hide chat widget after hours",
        "outside support hours",
      ],
    }),
    "customerWidget/general/widgetHoursEnabled": setting({
      label: "Widget hours enabled",
      description: "Uses availability hours for the customer widget.",
    }),
    "customerWidget/general/widgetHoursTimezone": setting({
      label: "Widget hours timezone",
      description: "Timezone used for customer widget availability hours.",
      valueSemantics: { inputKind: "timezone" },
    }),
    "customerWidget/general/widgetHours/monday/enabled": setting({
      label: "Monday widget hours enabled",
      description: "Enables Monday availability hours for the customer widget.",
    }),
    "customerWidget/general/widgetHours/monday/timeBlocks": setting({
      label: "Monday widget time blocks",
      description: "Monday availability time blocks for the customer widget.",
    }),
    "customerWidget/general/widgetHours/tuesday/enabled": setting({
      label: "Tuesday widget hours enabled",
      description:
        "Enables Tuesday availability hours for the customer widget.",
    }),
    "customerWidget/general/widgetHours/tuesday/timeBlocks": setting({
      label: "Tuesday widget time blocks",
      description: "Tuesday availability time blocks for the customer widget.",
    }),
    "customerWidget/general/widgetHours/wednesday/enabled": setting({
      label: "Wednesday widget hours enabled",
      description:
        "Enables Wednesday availability hours for the customer widget.",
    }),
    "customerWidget/general/widgetHours/wednesday/timeBlocks": setting({
      label: "Wednesday widget time blocks",
      description:
        "Wednesday availability time blocks for the customer widget.",
    }),
    "customerWidget/general/widgetHours/thursday/enabled": setting({
      label: "Thursday widget hours enabled",
      description:
        "Enables Thursday availability hours for the customer widget.",
    }),
    "customerWidget/general/widgetHours/thursday/timeBlocks": setting({
      label: "Thursday widget time blocks",
      description: "Thursday availability time blocks for the customer widget.",
    }),
    "customerWidget/general/widgetHours/friday/enabled": setting({
      label: "Friday widget hours enabled",
      description: "Enables Friday availability hours for the customer widget.",
    }),
    "customerWidget/general/widgetHours/friday/timeBlocks": setting({
      label: "Friday widget time blocks",
      description: "Friday availability time blocks for the customer widget.",
    }),
    "customerWidget/general/widgetHours/saturday/enabled": setting({
      label: "Saturday widget hours enabled",
      description:
        "Enables Saturday availability hours for the customer widget.",
    }),
    "customerWidget/general/widgetHours/saturday/timeBlocks": setting({
      label: "Saturday widget time blocks",
      description: "Saturday availability time blocks for the customer widget.",
    }),
    "customerWidget/general/widgetHours/sunday/enabled": setting({
      label: "Sunday widget hours enabled",
      description: "Enables Sunday availability hours for the customer widget.",
    }),
    "customerWidget/general/widgetHours/sunday/timeBlocks": setting({
      label: "Sunday widget time blocks",
      description: "Sunday availability time blocks for the customer widget.",
    }),
  }),

  ...supportGroup("support.concierge_handoff", {
    "concierge/ticketHandoffSettings/handoffType": setting({
      label: "Handoff type",
      description: "How Concierge hands conversations off to support.",
      relatedGroups: ["support.live_chat_widget"],
      valueSemantics: { options: SUPPORT_TICKET_HANDOFF_TYPE_OPTIONS },
      risk: "medium",
    }),
    "concierge/ticketHandoffSettings/emailHandoffMessage": setting({
      label: "Email handoff message",
      description:
        "Message sent when Concierge hands a conversation off by email.",
      relatedGroups: ["support.live_chat_widget"],
      valueSemantics: { inputKind: "textarea" },
      synonyms: [
        "concierge email handoff message",
        "human handoff email message",
        "ticket handoff email copy",
      ],
    }),
    "concierge/ticketHandoffSettings/collectCustomerEmailBeforeHandoff":
      setting({
        label: "Collect email before handoff",
        description: "Collects the customer's email before Concierge handoff.",
      }),
    "concierge/ticketHandoffSettings/forwardToExternalEmail": setting({
      label: "Forward handoff externally",
      description: "Forwards Concierge handoffs to an external email address.",
      risk: {
        level: "medium",
        reason:
          "Can send customer conversations to an external email destination.",
      },
    }),
  }),
} as const;

export type SupportFlatTeamSettingKey =
  keyof typeof SupportFlatSettingCatalogRegistry & string;

export const SupportFlatTeamSettingKeys = Object.keys(
  SupportFlatSettingCatalogRegistry,
) as SupportFlatTeamSettingKey[];

const SupportFlatTeamSettingKeySet = new Set<string>(
  SupportFlatTeamSettingKeys,
);

export function isSupportFlatTeamSettingKey(
  key: string,
): key is SupportFlatTeamSettingKey {
  return SupportFlatTeamSettingKeySet.has(key);
}

export const SupportFlatTeamSettingCatalogDefinitions =
  SupportFlatTeamSettingKeys.map((key) => {
    const definition = SupportFlatSettingCatalogRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: SupportFlatTeamSettingGroupById[definition.group],
        resolveAgentEditability: createSupportSettingAgentEditability,
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    SupportFlatTeamSettingKey,
    SupportFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<SupportFlatTeamSettingKey>
  >[];

export const SupportFlatTeamSettingMetadataByKey = Object.fromEntries(
  SupportFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<SupportFlatTeamSettingKey, SettingMetadata>;

export const SupportFlatTeamSettingGroupIdByKey = Object.fromEntries(
  SupportFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.groupId,
  ]),
) as Record<SupportFlatTeamSettingKey, SupportFlatTeamSettingGroupId>;

function createSupportSettingAgentEditability(
  context: SettingMetadataDefaultsContext<SupportSettingRegistryEntry>,
): SettingAgentEditability {
  const defaultEditability = getDefaultSettingAgentEditability(context);
  if (defaultEditability.mode !== "safeToApply") {
    return defaultEditability;
  }

  return {
    mode: "requiresApproval",
    reason:
      "Support setting changes should be reviewed with the merchant before saving.",
  };
}
